import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiEdit2, FiCheck, FiX, FiEye, FiEyeOff, FiRefreshCw } from 'react-icons/fi';

const VILLA_LABEL = {
  '3bhk-garden-escape-lonavala': 'Artistic Villa (3BHK)',
  '4bhk-garden-retreat-lonavala': 'Reunion Villa (4BHK)',
  '5bhk-garden-delight-lonavala': 'Richie Villa (5BHK)',
  '12bhk-celebration-villa-lonavala': 'Celebration Villa (12BHK)',
};

/**
 * Shared moderation panel used by both:
 *  - AdminDashboard → PublishedReviewsAdmin (admin JWT auth)
 *  - FeedbackApp host role (feedback token auth)
 *
 * Props:
 *  - apiBase: URL prefix WITHOUT trailing slash, e.g. `${API}/api/admin/reviews`
 *             or `${API}/api/feedback-app/{token}/reviews`
 *  - authConfig: axios config object (headers) — pass {} for token-in-URL flows
 *  - variant: 'admin' | 'host' — controls dark/light styling
 */
export default function ReviewModerationPanel({ apiBase, authConfig = {}, variant = 'admin' }) {
  const isHost = variant === 'host';
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get(`${apiBase}?include_unpublished=true`, authConfig)
      .then(r => setReviews(r.data.reviews || []))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 3500); };

  const startEdit = (r) => { setEditingId(r.id); setEditText(r.remark_text); setEditName(r.guest_name || ''); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); setEditName(''); };

  const saveEdit = async (id) => {
    setError('');
    try {
      await axios.patch(`${apiBase}/${id}`, { remark_text: editText, guest_name: editName }, authConfig);
      cancelEdit(); load();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
  };

  const toggle = async (r) => {
    setError('');
    const endpoint = r.is_published === false ? 'republish' : 'unpublish';
    try {
      await axios.post(`${apiBase}/${r.id}/${endpoint}`, {}, authConfig);
      load();
    } catch (e) { setError(e.response?.data?.detail || 'Action failed'); }
  };

  const toggleSelect = (id) => {
    setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const selectAll = () => {
    if (selected.size === reviews.length) setSelected(new Set());
    else setSelected(new Set(reviews.map(r => r.id)));
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`${action === 'unpublish' ? 'Unpublish' : 'Republish'} ${selected.size} review(s)?`)) return;
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${apiBase}/bulk-action`, { action, ids: Array.from(selected) }, authConfig);
      setSelected(new Set());
      showFlash(`${action === 'unpublish' ? 'Unpublished' : 'Republished'} ${res.data.modified} review(s)`);
      load();
    } catch (e) { setError(e.response?.data?.detail || 'Bulk action failed'); }
    setBusy(false);
  };

  const refreshRemarks = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Refresh the remark pool? Unused LLM remarks will be replaced with freshly-generated ones.')) return;
    setBusy(true); setError('');
    try {
      const res = await axios.post(`${apiBase}/refresh-remarks`, {}, authConfig);
      showFlash(`Pool refreshed — ${res.data.deactivated} replaced, ${res.data.unused_now} fresh remarks live`);
    } catch (e) { setError(e.response?.data?.detail || 'Refresh failed'); }
    setBusy(false);
  };

  const allSelected = reviews.length > 0 && selected.size === reviews.length;
  const someSelected = selected.size > 0 && !allSelected;

  // Dark vs light styling
  const S = isHost ? {
    cardBg: 'bg-[#1B2432]', cardBorder: 'border-white/10', textBase: 'text-white', textMuted: 'text-white/70', textSub: 'text-white/50',
    barBg: 'bg-[#1B2432]', barBorder: 'border-white/10',
    editBg: 'bg-[#0F1420]', editBorder: 'border-white/10', editText: 'text-white',
    refreshBg: 'bg-transparent border border-[#F57C20]/40 hover:bg-[#F57C20]/10 text-[#F57C20]',
  } : {
    cardBg: 'bg-white', cardBorder: 'border-[#1A1A2E]/10', textBase: 'text-[#1A1A2E]', textMuted: 'text-[#6B7280]', textSub: 'text-[#9CA3AF]',
    barBg: 'bg-white', barBorder: 'border-[#1A1A2E]/10',
    editBg: 'bg-white', editBorder: 'border-[#1A1A2E]/20', editText: 'text-[#1A1A2E]',
    refreshBg: 'bg-white border border-[#F57C20]/30 hover:bg-[#F57C20]/5 text-[#F57C20]',
  };

  return (
    <div data-testid={`moderation-panel-${variant}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
        <p className={`${S.textMuted} text-xs`}>Guest-consented 5★ reviews on the Home page wall. Unpublish or edit off-message ones instantly.</p>
        <button onClick={refreshRemarks} disabled={busy}
          data-testid="mod-refresh-remarks"
          className={`${S.refreshBg} text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 self-start whitespace-nowrap`}>
          <FiRefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Refresh Remark Pool
        </button>
      </div>

      {flash && <div className="bg-green-50 border border-green-200 text-green-700 text-xs px-3 py-2 rounded mb-3" data-testid="mod-flash">{flash}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded mb-3" data-testid="mod-error">{error}</div>}

      {reviews.length > 0 && (
        <div className={`${S.barBg} rounded-lg border ${S.barBorder} px-3 py-2 mb-3 flex items-center justify-between gap-2`} data-testid="mod-bulk-bar">
          <label className={`flex items-center gap-2 text-xs ${S.textBase} cursor-pointer`}>
            <input type="checkbox"
              checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected; }}
              onChange={selectAll}
              data-testid="mod-select-all"
              className="w-4 h-4 accent-[#F57C20]" />
            {selected.size > 0
              ? <span><span className="font-semibold">{selected.size}</span> selected</span>
              : <span className={S.textSub}>Select all ({reviews.length})</span>}
          </label>
          {selected.size > 0 && (
            <div className="flex gap-2">
              <button onClick={() => bulkAction('unpublish')} disabled={busy}
                data-testid="mod-bulk-unpublish"
                className="bg-red-600 hover:bg-red-700 text-white text-[11px] font-semibold px-3 py-1.5 rounded disabled:opacity-50 flex items-center gap-1">
                <FiEyeOff className="w-3.5 h-3.5" /> Unpublish
              </button>
              <button onClick={() => bulkAction('republish')} disabled={busy}
                data-testid="mod-bulk-republish"
                className="bg-green-600 hover:bg-green-700 text-white text-[11px] font-semibold px-3 py-1.5 rounded disabled:opacity-50 flex items-center gap-1">
                <FiEye className="w-3.5 h-3.5" /> Republish
              </button>
            </div>
          )}
        </div>
      )}

      {loading && <p className={`${S.textMuted} text-xs`}>Loading…</p>}
      {!loading && reviews.length === 0 && (
        <div className={`${S.cardBg} rounded-lg p-6 text-center border ${S.cardBorder}`}>
          <p className={`${S.textMuted} text-xs`}>No published reviews yet. As 5★ guests opt-in via the feedback app, they&apos;ll appear here.</p>
        </div>
      )}

      <div className="space-y-2">
        {reviews.map(r => {
          const hidden = r.is_published === false;
          const isEditing = editingId === r.id;
          const isSelected = selected.has(r.id);
          return (
            <div key={r.id} data-testid={`mod-review-${r.id}`}
              className={`${S.cardBg} rounded-lg p-3 border shadow-sm ${isSelected ? 'border-[#F57C20] ring-1 ring-[#F57C20]/30' : hidden ? 'border-gray-400/40 opacity-60' : S.cardBorder}`}>
              <div className="flex items-start gap-2">
                <input type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelect(r.id)}
                  data-testid={`mod-select-${r.id}`}
                  className="w-4 h-4 accent-[#F57C20] mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <>
                      <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                        data-testid={`mod-edit-name-${r.id}`} maxLength={40} placeholder="Guest name"
                        className={`w-full text-xs px-2 py-1.5 rounded border ${S.editBorder} ${S.editBg} ${S.editText} mb-2`} />
                      <textarea value={editText} onChange={e => setEditText(e.target.value)}
                        data-testid={`mod-edit-text-${r.id}`} rows={3} maxLength={400}
                        className={`w-full text-xs px-2 py-1.5 rounded border ${S.editBorder} ${S.editBg} ${S.editText}`} />
                    </>
                  ) : (
                    <>
                      <p className={`${S.textBase} text-xs italic`}>&ldquo;{r.remark_text}&rdquo;</p>
                      <p className={`${S.textMuted} text-[10px] mt-1`}>
                        — <span className="font-semibold">{r.guest_name || 'Verified Guest'}</span>
                        {VILLA_LABEL[r.villa_id] && <span> · {VILLA_LABEL[r.villa_id]}</span>}
                        <span> · {new Date(r.published_at).toLocaleDateString()}</span>
                        {hidden && <span className="ml-2 bg-gray-400/30 px-1.5 py-0.5 rounded text-[9px]">HIDDEN</span>}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  {isEditing ? (
                    <>
                      <button onClick={() => saveEdit(r.id)} data-testid={`mod-save-${r.id}`}
                        className="bg-green-600 hover:bg-green-700 text-white p-1.5 rounded text-xs" title="Save">
                        <FiCheck className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={cancelEdit} data-testid={`mod-cancel-${r.id}`}
                        className="bg-gray-200 hover:bg-gray-300 text-[#1A1A2E] p-1.5 rounded text-xs" title="Cancel">
                        <FiX className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(r)} data-testid={`mod-edit-${r.id}`}
                        className="bg-[#F57C20] hover:bg-[#e06b15] text-white p-1.5 rounded text-xs" title="Edit">
                        <FiEdit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => toggle(r)} data-testid={`mod-toggle-${r.id}`}
                        className={`${hidden ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white p-1.5 rounded text-xs`}
                        title={hidden ? 'Republish' : 'Unpublish'}>
                        {hidden ? <FiEye className="w-3.5 h-3.5" /> : <FiEyeOff className="w-3.5 h-3.5" />}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
